import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { first } from 'rxjs/operators';
import { FacilityDobryAgentService } from '../../../shared/services/facility-dobry-agent.service';
import {
  DobryAgentBusinessCardNotificationSettings,
  DobryAgentNotificationRecipient,
} from '../../../shared/interface/facility-dobry-agent';

@Component({
  selector: 'app-dobry-agent-notification-settings',
  templateUrl: './dobry-agent-notification-settings.component.html',
  styleUrls: ['./dobry-agent-notification-settings.component.scss'],
})
export class DobryAgentNotificationSettingsComponent implements OnInit {
  form: FormGroup;

  loading = false;
  processingRequest = false;
  editMode = false;

  recipients: DobryAgentNotificationRecipient[] = [];
  private updatingFromUserSelect = false;

  constructor(
    private readonly fb: FormBuilder,
    private readonly snackBar: MatSnackBar,
    private readonly facilityDobryAgentService: FacilityDobryAgentService,
  ) {
    this.form = this.fb.group({
      recipients: this.fb.array([]),
    });
  }

  ngOnInit(): void {
    this.loadSettings();
  }

  get recipientsArray(): FormArray {
    return this.form.get('recipients') as FormArray;
  }

  getRecipientGroup(index: number): FormGroup {
    return this.recipientsArray.at(index) as FormGroup;
  }

  getEmailControl(index: number): FormControl {
    return this.getRecipientGroup(index).get('email') as FormControl;
  }

  getUserControl(index: number): FormControl {
    return this.getRecipientGroup(index).get('userUuid') as FormControl;
  }
  loadSettings(): void {
    this.loading = true;

    this.facilityDobryAgentService.getBusinessCardNotificationRecipients()
      .pipe(first())
      .subscribe({
        next: (settings: DobryAgentBusinessCardNotificationSettings) => {
          this.loading = false;

          this.recipients = this.normalizeRecipients(
            settings.recipients ?? this.mapEmailsToRecipients(settings.emails ?? [])
          );

          this.patchRecipients(this.recipients);
          this.editMode = false;
        },
        error: () => {
          this.loading = false;

          this.snackBar.open('Nie udało się pobrać ustawień powiadomień DobryAgent', '', {
            duration: 3000,
          });
        },
      });
  }

  startEdit(): void {
    this.patchRecipients(this.recipients);
    this.editMode = true;
  }

  cancel(): void {
    this.patchRecipients(this.recipients);
    this.editMode = false;
  }

  addRecipient(): void {
    this.recipientsArray.push(this.createRecipientGroup({
      userUuid: null,
      email: '',
      phoneNumber: null,
      fullName: null,
    }));
  }

  removeRecipient(index: number): void {
    this.recipientsArray.removeAt(index);

    if (this.recipientsArray.length === 0) {
      this.addRecipient();
    }
  }

  selectedUser(user: any, index: number): void {
    const recipient = this.mapUserToRecipient(user);

    if (!recipient.email) {
      this.snackBar.open('Wybrany użytkownik nie ma adresu e-mail', '', {
        duration: 2500,
      });

      return;
    }

    if (this.hasEmail(recipient.email, index)) {
      this.snackBar.open('Ten adres e-mail jest już dodany w innym wierszu', '', {
        duration: 2500,
      });

      return;
    }

    const group = this.getRecipientGroup(index);

    this.updatingFromUserSelect = true;

    group.patchValue({
      userUuid: recipient.userUuid,
      email: recipient.email,
      phoneNumber: recipient.phoneNumber,
      fullName: recipient.fullName,
    }, { emitEvent: false });

    group.markAsDirty();
    group.updateValueAndValidity();

    this.updatingFromUserSelect = false;
  }


  onEmailManualChange(index: number): void {
    if (this.updatingFromUserSelect) {
      return;
    }

    const group = this.getRecipientGroup(index);

    group.patchValue({
      userUuid: null,
      fullName: null,
    }, { emitEvent: false });
  }
  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();

      this.snackBar.open('Popraw błędy w formularzu', '', {
        duration: 2500,
      });

      return;
    }

    const recipients = this.prepareRecipientsPayload();

    if (!recipients.length) {
      this.snackBar.open('Podaj przynajmniej jeden adres e-mail', '', {
        duration: 2500,
      });

      return;
    }

    this.processingRequest = true;

    this.facilityDobryAgentService.updateBusinessCardNotificationRecipients({
      recipients,
    }).pipe(first())
      .subscribe({
        next: (response) => {
          this.processingRequest = false;

          this.recipients = this.normalizeRecipients(response.recipients ?? recipients);
          this.patchRecipients(this.recipients);
          this.editMode = false;

          this.snackBar.open('Ustawienia powiadomień zostały zapisane', '', {
            duration: 2500,
          });
        },
        error: () => {
          this.processingRequest = false;

          this.snackBar.open('Nie udało się zapisać ustawień powiadomień', '', {
            duration: 3000,
          });
        },
      });
  }

  private patchRecipients(recipients: DobryAgentNotificationRecipient[]): void {
    this.recipientsArray.clear();

    if (!recipients.length) {
      this.addRecipient();
      return;
    }

    recipients.forEach((recipient) => {
      this.recipientsArray.push(this.createRecipientGroup(recipient));
    });
  }

  private createRecipientGroup(recipient: DobryAgentNotificationRecipient): FormGroup {
    return this.fb.group({
      userUuid: [recipient.userUuid ?? null],
      email: [recipient.email ?? '', [
        Validators.required,
        Validators.email,
      ]],
      phoneNumber: [recipient.phoneNumber ?? ''],
      fullName: [recipient.fullName ?? null],
    });
  }

  private prepareRecipientsPayload(): DobryAgentNotificationRecipient[] {
    return this.normalizeRecipients(this.recipientsArray.getRawValue() ?? []);
  }

  private normalizeRecipients(recipients: DobryAgentNotificationRecipient[]): DobryAgentNotificationRecipient[] {
    const normalized = (recipients ?? [])
      .map((recipient: DobryAgentNotificationRecipient) => ({
        userUuid: recipient.userUuid ?? null,
        email: String(recipient.email ?? '').trim(),
        phoneNumber: String(recipient.phoneNumber ?? '').trim() || null,
        fullName: recipient.fullName ?? null,
      }))
      .filter((recipient) => recipient.email !== '');

    return normalized.filter((recipient, index, items) => {
      return items.findIndex((item) => {
        return item.email.toLowerCase() === recipient.email.toLowerCase();
      }) === index;
    });
  }

  private mapEmailsToRecipients(emails: string[]): DobryAgentNotificationRecipient[] {
    return (emails ?? []).map((email) => ({
      userUuid: null,
      email,
      phoneNumber: null,
      fullName: null,
    }));
  }

  private mapUserToRecipient(user: any): DobryAgentNotificationRecipient {
    if (!user) {
      return {
        userUuid: null,
        email: '',
        phoneNumber: null,
        fullName: null,
      };
    }

    return {
      userUuid: user.uuid ?? null,
      email: String(user.email ?? '').trim(),
      phoneNumber: this.getUserPhoneNumber(user),
      fullName: this.getUserFullName(user),
    };
  }

  private getUserPhoneNumber(user: any): string | null {
    return String(
      user.phone ?? ''
    ).trim() || null;
  }

  private getUserFullName(user: any): string | null {
    const fullName = [
      user.firstName ?? null,
      user.lastName ?? null,
    ]
      .filter(Boolean)
      .join(' ')
      .trim();

    if (fullName) {
      return fullName;
    }

    return user.fullName ?? user.name ?? null;
  }

  private hasEmail(email: string, currentIndex: number): boolean {
    const normalizedEmail = String(email ?? '').trim().toLowerCase();

    return this.recipientsArray.controls.some((control, index) => {
      if (index === currentIndex) {
        return false;
      }

      const group = control as FormGroup;

      return String(group.get('email')?.value ?? '').trim().toLowerCase() === normalizedEmail;
    });
  }
}
